// Static demo-mode data for EmployeeList.component.jsx.
// The Employee List page is demo-only (rendered exclusively under the
// `demo` build/env — see .env.demo REACT_APP_DEMO_MODE) so this file is the
// single source of truth for company/department/roster data; nothing in the
// component reaches Firestore/Redux/services for this page any more.
//
// Shapes:
//  - DEMO_COMPANY_OPTIONS / DEMO_TEAM_OPTIONS: { label, value } (dropdown options)
//  - DEMO_EMPLOYEES: one entry per employee. `teamIds` lists every department
//    the employee belongs to (drives the roster-per-team filter, the delete
//    flow's "only one team" rule, and the "Add to another team" candidate
//    list); `activeTeamId` is which of those teams currently receives their
//    time/activity tracking. `employeeId`/`timeZone`/`deskId`/`guid`/`joinedOn`
//    match the fields the "Employee details" popup used to fetch separately
//    via fetchEmployeeExtendedDetails (see src/services/employeeMemberEnrichment.js)
//    and now reads directly off the row.

export const DEMO_COMPANY_OPTIONS = [
  { label: 'Meridian Labs', value: 'demo-company' },
];

export const DEMO_SELECTED_COMPANY = DEMO_COMPANY_OPTIONS[0];

export const DEMO_TEAM_OPTIONS = [
  { label: 'Engineering', value: 'demo-team-engineering' },
  { label: 'Sales', value: 'demo-team-sales' },
  { label: 'Customer Support', value: 'demo-team-support' },
];

export const DEMO_SELECTED_TEAM = DEMO_TEAM_OPTIONS[0];

// Matches fetchEmployeeExtendedDetails' return shape (see
// src/services/employeeMemberEnrichment.js) for the logged-in demo user
// ('demo-user' below) — consumed by Profile/ProfileEditForm.component.jsx
// and Profile/ProfileHeaderCard.component.jsx in place of the real Firestore
// query, which would otherwise error/resolve empty for the demo profile.
export const DEMO_MY_EXTENDED_DETAILS = {
  employeeId: 'EMP-1001',
  timeZone: 'America/New_York',
  guid: 'a1b2c3d4-e5f6-4a1b-8c2d-000000000001',
  deskId: 'DSK-2001',
  loginAllowed: true,
  activeTeamId: 'demo-team-engineering',
  createdAt: { toDate: () => new Date('2024-08-12') },
};

const ENGINEERING = DEMO_TEAM_OPTIONS[0].value;
const SALES = DEMO_TEAM_OPTIONS[1].value;
const SUPPORT = DEMO_TEAM_OPTIONS[2].value;
const ALL_TEAM_IDS = DEMO_TEAM_OPTIONS.map((t) => t.value);

// Consumed by the DataTable rows (name/role/email columns), roleCounts
// StatCards, CSV export, and the "Employee details" / "Edit employee"
// dialogs (employeeId/timeZone/deskId/guid/joinedOn read straight off the row).
export const DEMO_EMPLOYEES = [
  {
    userId: 'demo-user',
    name: 'Ryan Mitchell',
    email: 'ryan.mitchell@meridianlabs.com',
    role: 'Admin',
    companyId: 'demo-company',
    teamIds: ALL_TEAM_IDS,
    activeTeamId: ENGINEERING,
    employeeId: 'EMP-1001',
    timeZone: 'America/New_York',
    deskId: 'DSK-2001',
    guid: 'a1b2c3d4-e5f6-4a1b-8c2d-000000000001',
    joinedOn: '2024-08-12',
  },
  {
    userId: 'demo-emp-1',
    name: 'Rachel Turner',
    email: 'rachel.turner@meridianlabs.com',
    role: 'Manager',
    companyId: 'demo-company',
    teamIds: [ENGINEERING, SALES],
    activeTeamId: ENGINEERING,
    employeeId: 'EMP-1002',
    timeZone: 'America/Denver',
    deskId: 'DSK-2002',
    guid: 'a1b2c3d4-e5f6-4a1b-8c2d-000000000002',
    joinedOn: '2024-11-03',
  },
  {
    userId: 'demo-emp-2',
    name: 'Michael Chen',
    email: 'michael.chen@meridianlabs.com',
    role: 'Member',
    companyId: 'demo-company',
    teamIds: [ENGINEERING],
    activeTeamId: ENGINEERING,
    employeeId: 'EMP-1003',
    timeZone: 'America/Los_Angeles',
    deskId: 'DSK-2003',
    guid: 'a1b2c3d4-e5f6-4a1b-8c2d-000000000003',
    joinedOn: '2025-01-20',
  },
  {
    userId: 'demo-emp-3',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@meridianlabs.com',
    role: 'Member',
    companyId: 'demo-company',
    teamIds: [SALES],
    activeTeamId: SALES,
    employeeId: 'EMP-1004',
    timeZone: 'Europe/London',
    deskId: 'DSK-2004',
    guid: 'a1b2c3d4-e5f6-4a1b-8c2d-000000000004',
    joinedOn: '2025-02-14',
  },
  {
    userId: 'demo-emp-4',
    name: 'David Kim',
    email: 'david.kim@meridianlabs.com',
    role: 'Member',
    companyId: 'demo-company',
    teamIds: [SUPPORT],
    activeTeamId: SUPPORT,
    employeeId: 'EMP-1005',
    timeZone: 'Asia/Singapore',
    deskId: 'DSK-2005',
    guid: 'a1b2c3d4-e5f6-4a1b-8c2d-000000000005',
    joinedOn: '2025-03-05',
  },
  {
    userId: 'demo-emp-5',
    name: 'Emma Williams',
    email: 'emma.williams@meridianlabs.com',
    role: 'Manager',
    companyId: 'demo-company',
    teamIds: [SALES, SUPPORT],
    activeTeamId: SALES,
    employeeId: 'EMP-1006',
    timeZone: 'Australia/Sydney',
    deskId: 'DSK-2006',
    guid: 'a1b2c3d4-e5f6-4a1b-8c2d-000000000006',
    joinedOn: '2024-09-28',
  },
  {
    userId: 'demo-emp-6',
    name: 'Carlos Martinez',
    email: 'carlos.martinez@meridianlabs.com',
    role: 'Member',
    companyId: 'demo-company',
    teamIds: [ENGINEERING, SUPPORT],
    activeTeamId: SUPPORT,
    employeeId: 'EMP-1007',
    timeZone: 'Europe/Berlin',
    deskId: 'DSK-2007',
    guid: 'a1b2c3d4-e5f6-4a1b-8c2d-000000000007',
    joinedOn: '2025-04-17',
  },
  {
    userId: 'demo-emp-7',
    name: 'Natalie Brooks',
    email: 'natalie.brooks@meridianlabs.com',
    role: 'Member',
    companyId: 'demo-company',
    teamIds: [ENGINEERING],
    activeTeamId: ENGINEERING,
    employeeId: 'EMP-1008',
    timeZone: 'Europe/Amsterdam',
    deskId: 'DSK-2008',
    guid: 'a1b2c3d4-e5f6-4a1b-8c2d-000000000008',
    joinedOn: '2025-05-30',
  },
  {
    userId: 'demo-emp-8',
    name: 'James Wilson',
    email: 'james.wilson@meridianlabs.com',
    role: 'Manager',
    companyId: 'demo-company',
    teamIds: [SUPPORT],
    activeTeamId: SUPPORT,
    employeeId: 'EMP-1009',
    timeZone: 'America/Chicago',
    deskId: 'DSK-2009',
    guid: 'a1b2c3d4-e5f6-4a1b-8c2d-000000000009',
    joinedOn: '2024-12-09',
  },
  {
    userId: 'demo-emp-9',
    name: 'Wei Zhang',
    email: 'wei.zhang@meridianlabs.com',
    role: 'Member',
    companyId: 'demo-company',
    teamIds: [SALES],
    activeTeamId: SALES,
    employeeId: 'EMP-1010',
    timeZone: 'Asia/Shanghai',
    deskId: 'DSK-2010',
    guid: 'a1b2c3d4-e5f6-4a1b-8c2d-000000000010',
    joinedOn: '2025-06-11',
  },
  {
    userId: 'demo-emp-10',
    name: 'Fatima Al-Sayed',
    email: 'fatima.alsayed@meridianlabs.com',
    role: 'Member',
    companyId: 'demo-company',
    teamIds: [ENGINEERING, SALES, SUPPORT],
    activeTeamId: ENGINEERING,
    employeeId: 'EMP-1011',
    timeZone: 'Asia/Dubai',
    deskId: 'DSK-2011',
    guid: 'a1b2c3d4-e5f6-4a1b-8c2d-000000000011',
    joinedOn: '2024-10-22',
  },
  {
    userId: 'demo-emp-11',
    name: 'Lucas Rodriguez',
    email: 'lucas.rodriguez@meridianlabs.com',
    role: 'Member',
    companyId: 'demo-company',
    teamIds: [SUPPORT],
    activeTeamId: SUPPORT,
    employeeId: 'EMP-1012',
    timeZone: 'America/Sao_Paulo',
    deskId: 'DSK-2012',
    guid: 'a1b2c3d4-e5f6-4a1b-8c2d-000000000012',
    joinedOn: '2025-07-01',
  },
];

// Fake import-run history for the QuickBooks / Salesforce / Sage HR / Zoho
// People tabs (IntegrationImportTab.component.jsx). Keyed by the same
// `source` key the tab already receives as a prop. Each source has its
// most-recent run first (mirrors the Firestore query's
// `.orderBy('importedAt', 'desc').limit(1)` — the component just reads
// `logs[0]` in demo mode instead of subscribing).
export const DEMO_IMPORT_LOGS = {
  quickbooks: {
    logs: [
      {
        id: 'demo-qb-log-2',
        importedAt: '2026-07-10T09:15:00.000Z',
        importedBy: { name: 'Ryan Mitchell', userId: 'demo-user' },
        status: 'completed',
        stats: { imported: 1, invited: 1, mapped: 1, skipped: 1, errors: 1 },
        employees: [
          {
            externalId: 'QB-8841',
            name: 'Nicole Bennett',
            email: 'nicole.bennett@meridianlabs.com',
            outcome: 'imported',
          },
          {
            externalId: 'QB-8842',
            name: 'Tom Becker',
            email: 'tom.becker@meridianlabs.com',
            outcome: 'invited',
          },
          {
            externalId: 'QB-8843',
            name: 'Rachel Turner',
            email: 'rachel.turner@meridianlabs.com',
            outcome: 'mapped',
          },
          {
            externalId: 'QB-8844',
            name: 'Old Contractor',
            email: '',
            outcome: 'skipped',
          },
          {
            externalId: 'QB-8845',
            name: 'Broken Record',
            email: 'broken@meridianlabs.com',
            outcome: 'error',
            reason: 'Missing required desk ID mapping.',
          },
        ],
      },
      {
        id: 'demo-qb-log-1',
        importedAt: '2026-06-02T14:40:00.000Z',
        importedBy: { name: 'Ryan Mitchell', userId: 'demo-user' },
        status: 'completed',
        stats: { imported: 5, invited: 0, mapped: 0, skipped: 0, errors: 0 },
        employees: [],
      },
    ],
  },
  salesforce: {
    logs: [
      {
        id: 'demo-sf-log-2',
        importedAt: '2026-07-08T11:05:00.000Z',
        importedBy: { name: 'Ryan Mitchell', userId: 'demo-user' },
        status: 'completed',
        stats: { imported: 2, invited: 2, mapped: 1, skipped: 0, errors: 0 },
        employees: [
          {
            externalId: 'SF-0031',
            name: 'Emma Williams',
            email: 'emma.williams@meridianlabs.com',
            outcome: 'mapped',
          },
          {
            externalId: 'SF-0032',
            name: 'Grace Lee',
            email: 'grace.lee@meridianlabs.com',
            outcome: 'imported',
          },
          {
            externalId: 'SF-0033',
            name: 'Ben Foster',
            email: 'ben.foster@meridianlabs.com',
            outcome: 'imported',
          },
          {
            externalId: 'SF-0034',
            name: 'Ethan Cooper',
            email: 'ethan.cooper@meridianlabs.com',
            outcome: 'invited',
          },
          {
            externalId: 'SF-0035',
            name: 'Olivia Grant',
            email: 'olivia.grant@meridianlabs.com',
            outcome: 'invited',
          },
        ],
      },
      {
        id: 'demo-sf-log-1',
        importedAt: '2026-05-19T08:20:00.000Z',
        importedBy: { name: 'Ryan Mitchell', userId: 'demo-user' },
        status: 'completed',
        stats: { imported: 4, invited: 0, mapped: 0, skipped: 1, errors: 0 },
        employees: [],
      },
    ],
  },
  sagehr: {
    logs: [
      {
        id: 'demo-sagehr-log-2',
        importedAt: '2026-07-12T16:30:00.000Z',
        importedBy: { name: 'Ryan Mitchell', userId: 'demo-user' },
        status: 'completed',
        stats: { imported: 1, invited: 1, mapped: 3, skipped: 1, errors: 0 },
        employees: [
          {
            externalId: 'SHR-501',
            name: 'James Wilson',
            email: 'james.wilson@meridianlabs.com',
            outcome: 'mapped',
          },
          {
            externalId: 'SHR-502',
            name: 'Carlos Martinez',
            email: 'carlos.martinez@meridianlabs.com',
            outcome: 'mapped',
          },
          {
            externalId: 'SHR-503',
            name: 'Wei Zhang',
            email: 'wei.zhang@meridianlabs.com',
            outcome: 'mapped',
          },
          {
            externalId: 'SHR-504',
            name: 'New Hire Pending',
            email: 'pending.hire@meridianlabs.com',
            outcome: 'invited',
          },
          {
            externalId: 'SHR-505',
            name: 'Contractor Record',
            email: '',
            outcome: 'skipped',
          },
          {
            externalId: 'SHR-506',
            name: 'Fatima Al-Sayed',
            email: 'fatima.alsayed@meridianlabs.com',
            outcome: 'imported',
          },
        ],
      },
      {
        id: 'demo-sagehr-log-1',
        importedAt: '2026-04-01T10:00:00.000Z',
        importedBy: { name: 'Ryan Mitchell', userId: 'demo-user' },
        status: 'completed',
        stats: { imported: 6, invited: 0, mapped: 0, skipped: 0, errors: 0 },
        employees: [],
      },
    ],
  },
  zohopeople: {
    logs: [
      {
        id: 'demo-zoho-log-2',
        importedAt: '2026-07-14T13:45:00.000Z',
        importedBy: { name: 'Ryan Mitchell', userId: 'demo-user' },
        status: 'completed',
        stats: { imported: 2, invited: 0, mapped: 2, skipped: 0, errors: 1 },
        employees: [
          {
            externalId: 'ZP-701',
            name: 'Natalie Brooks',
            email: 'natalie.brooks@meridianlabs.com',
            outcome: 'mapped',
          },
          {
            externalId: 'ZP-702',
            name: 'Lucas Rodriguez',
            email: 'lucas.rodriguez@meridianlabs.com',
            outcome: 'mapped',
          },
          {
            externalId: 'ZP-703',
            name: 'Hana Suzuki',
            email: 'hana.suzuki@meridianlabs.com',
            outcome: 'imported',
          },
          {
            externalId: 'ZP-704',
            name: 'Marcus Bell',
            email: 'marcus.bell@meridianlabs.com',
            outcome: 'imported',
          },
          {
            externalId: 'ZP-705',
            name: 'Duplicate Entry',
            email: 'dup@meridianlabs.com',
            outcome: 'error',
            reason: 'Employee already exists with a different desk ID.',
          },
        ],
      },
      {
        id: 'demo-zoho-log-1',
        importedAt: '2026-03-22T07:10:00.000Z',
        importedBy: { name: 'Ryan Mitchell', userId: 'demo-user' },
        status: 'completed',
        stats: { imported: 3, invited: 0, mapped: 0, skipped: 0, errors: 0 },
        employees: [],
      },
    ],
  },
};
